import { useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DataTable,
  FormInput,
  FormSelect,
  ImageUpload,
  PageHeader,
  StandardImage,
  useDataTable,
  type DataTableColumn,
} from "@/components/shared";
import {
  useAdminBanners,
  useCreateBannerMutation,
  useDeleteBannerMutation,
  useUpdateBannerMutation,
} from "@/hooks/queries";
import type { BannerDto } from "@/types/banner.dto";
import { BannerPosition, BannerType } from "@/types/banner.dto";
import { UploadFolder } from "@/services/filesService";
import { getApiErrorMessage } from "@/services/httpClient";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";

const typeOptions = [
  { label: "Home", value: String(BannerType.Home) },
  { label: "Category", value: String(BannerType.Category) },
  { label: "Store", value: String(BannerType.Store) },
  { label: "Promotional", value: String(BannerType.Promotional) },
  { label: "App", value: String(BannerType.App) },
];

const positionOptions = [
  { label: "Top", value: String(BannerPosition.Top) },
  { label: "Middle", value: String(BannerPosition.Middle) },
  { label: "Bottom", value: String(BannerPosition.Bottom) },
  { label: "Sidebar", value: String(BannerPosition.Sidebar) },
];

const emptyForm = {
  title: "",
  subtitle: "",
  imageUrl: "",
  linkUrl: "",
  type: String(BannerType.Home),
  position: String(BannerPosition.Top),
  displayOrder: 1,
  isActive: true,
};

const AdminBanners = () => {
  const { toast } = useToast();
  const { data, isLoading } = useAdminBanners();
  const createMutation = useCreateBannerMutation();
  const updateMutation = useUpdateBannerMutation();
  const deleteMutation = useDeleteBannerMutation();
  const banners = data?.items ?? [];
  const [sheetOpen, setSheetOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editing, setEditing] = useState<BannerDto | null>(null);
  const [form, setForm] = useState(emptyForm);

  const table = useDataTable<BannerDto>({
    data: banners,
    searchKeys: ["title"],
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, displayOrder: banners.length + 1 });
    setSheetOpen(true);
  };

  const openEdit = (banner: BannerDto) => {
    setEditing(banner);
    setForm({
      title: banner.title,
      subtitle: banner.subtitle ?? "",
      imageUrl: banner.imageUrl,
      linkUrl: banner.linkUrl ?? "",
      type: String(banner.type),
      position: String(banner.position),
      displayOrder: banner.displayOrder,
      isActive: banner.isActive,
    });
    setSheetOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.imageUrl.trim()) {
      toast({ title: "Title and banner image are required", variant: "destructive" });
      return;
    }
    const dto = {
      title: form.title,
      subtitle: form.subtitle || undefined,
      imageUrl: form.imageUrl,
      linkUrl: form.linkUrl || undefined,
      type: Number(form.type) as BannerType,
      position: Number(form.position) as BannerPosition,
      displayOrder: form.displayOrder,
      isActive: form.isActive,
    };

    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, dto });
        toast({ title: "Banner updated" });
      } else {
        await createMutation.mutateAsync(dto);
        toast({ title: "Banner created" });
      }
      setSheetOpen(false);
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const columns: DataTableColumn<BannerDto>[] = [
    {
      id: "image",
      header: "Preview",
      cell: (row) => (
        <StandardImage src={row.imageUrl} alt={row.title} standard="storeBanner" className="h-10 w-28" rounded="md" />
      ),
    },
    {
      id: "title",
      header: "Title",
      cell: (row) => (
        <div>
          <p className="font-medium">{row.title}</p>
          {row.subtitle && <p className="text-xs text-muted-foreground">{row.subtitle}</p>}
        </div>
      ),
    },
    { id: "type", header: "Type", cell: (row) => row.typeText },
    { id: "order", header: "Order", cell: (row) => row.displayOrder },
    {
      id: "status",
      header: "Status",
      cell: (row) => (
        <Badge variant={row.isActive ? "default" : "secondary"}>
          {row.isActive ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: "",
      align: "right",
      cell: (row) => (
        <div className="flex gap-1 justify-end">
          <Button variant="ghost" size="icon" onClick={() => openEdit(row)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setDeleteId(row.id)}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Banners"
        description="Manage homepage and promotional banners."
        actions={
          <Button onClick={openCreate} className="gap-2">
            <Plus className="h-4 w-4" /> Add Banner
          </Button>
        }
      />

      {isLoading ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : (
        <DataTable
          title={`All Banners (${banners.length})`}
          columns={columns}
          data={table.rows}
          getRowId={(row) => String(row.id)}
          search={table.state.search}
          onSearchChange={table.setSearch}
          searchPlaceholder="Search banners…"
          sort={table.state.sort}
          onSortChange={table.toggleSort}
          pagination={table.pagination}
          onPageChange={table.setPage}
          onPageSizeChange={table.setPageSize}
        />
      )}

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-xl">
          <SheetHeader className="border-b px-6 py-5 text-left">
            <SheetTitle>{editing ? "Edit Banner" : "New Banner"}</SheetTitle>
            <SheetDescription>
              {editing ? "Update banner image and placement." : "Create a promotional banner for the storefront."}
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            <FormInput label="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <FormInput label="Subtitle" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} />

            <div className="space-y-4 rounded-lg border border-border p-4">
              <p className="text-sm font-medium">Banner image</p>
              <ImageUpload
                label="Image *"
                variant="banner"
                layout="stacked"
                value={form.imageUrl}
                onChange={(url) => setForm({ ...form, imageUrl: url })}
                folder={UploadFolder.Banners}
              />
            </div>

            <FormInput label="Link URL" value={form.linkUrl} onChange={(e) => setForm({ ...form, linkUrl: e.target.value })} />
            <FormSelect label="Type" value={form.type} onValueChange={(v) => setForm({ ...form, type: v })} options={typeOptions} />
            <FormSelect label="Position" value={form.position} onValueChange={(v) => setForm({ ...form, position: v })} options={positionOptions} />
            <FormInput label="Display Order" type="number" value={String(form.displayOrder)} onChange={(e) => setForm({ ...form, displayOrder: Number(e.target.value) })} />
            <div className="flex items-center justify-between rounded-lg border p-3">
              <Label>Active</Label>
              <Switch checked={form.isActive} onCheckedChange={(v) => setForm({ ...form, isActive: v })} />
            </div>
          </div>

          <SheetFooter className="mt-auto border-t bg-background px-6 py-4 sm:flex-row sm:justify-end sm:gap-2">
            <Button variant="outline" onClick={() => setSheetOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={createMutation.isPending || updateMutation.isPending}>Save</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={deleteId != null} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete banner?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (deleteId == null) return;
                try {
                  await deleteMutation.mutateAsync(deleteId);
                  toast({ title: "Banner deleted" });
                } catch (error) {
                  toast({ title: getApiErrorMessage(error), variant: "destructive" });
                }
                setDeleteId(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminBanners;
